import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";

const NotificationsContext = createContext({ unread: 0, refresh: () => {}, markRead: () => {}, markAllRead: () => {} });

/** Feb 2026 — mirrors UnreadContext's pattern so the sidebar/topbar badge
 * and the Notifications page always share one live count instead of each
 * polling independently and drifting for up to 30s after a read action. */
export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const { data } = await api.get("/notifications", { params: { limit: 1 } });
      setUnread(data.unread || 0);
    } catch (err) {
      logger.debug("Notifications refresh failed:", err?.message || err);
    }
  }, [user]);

  useEffect(() => {
    if (!user || user === false) { setUnread(0); return; }
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [user, refresh]);

  const markRead = useCallback(async (nid) => {
    try {
      await api.post(`/notifications/${nid}/read`);
      setUnread((u) => Math.max(0, u - 1));
    } catch (err) {
      logger.warn("Notification mark-read failed:", err?.message || err);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setUnread(0);
    } catch (err) {
      logger.warn("Notification mark-all-read failed:", err?.message || err);
    }
  }, []);

  const value = useMemo(() => ({ unread, refresh, markRead, markAllRead }), [unread, refresh, markRead, markAllRead]);
  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export const useNotifications = () => useContext(NotificationsContext);
